import {
  INATTIVITA_SESSIONE_MS,
  ParametriRegolazioneSchema,
  RichiestaForzaturaSchema,
  SETPOINT_PROFILO,
  TIMEOUT_COMANDO_MS,
  applicaTaratura,
  mandataDaCurva,
  programmaTipico,
  programmaVuoto,
  fasciaAttiva,
  puntoDiRugiada,
  validaSetpoint,
  MAX_GENERATORI,
  type Allarme,
  type Canale,
  type CodiceAllarme,
  type Comando,
  type CredenzialiTecnico,
  type Energia,
  type EventoDispositivo,
  type EventoStato,
  type FunzioneImpianto,
  type Generatore,
  type Impianto,
  type ImpiantoSintesi,
  type Lettura,
  type LetturaCanale,
  type MotivoFallimento,
  type ParametriRegolazione,
  type PatchCanale,
  type ProgrammaOrario,
  type RichiestaForzatura,
  type RispostaComando,
  type SessioneTecnica,
  type SeveritaAllarme,
  type StatoAttuatore,
  type StatoImpianto,
  type StatoOta,
  type StatoSanitario,
  type StatoSorgente,
  type TipoCarico,
  type VoceRegistro,
  type Zona,
} from '@emacomfort/domain';
import {
  ErroreAutorizzazione,
  ErroreRichiesta,
  type AreaTecnicaClient,
  type Ascoltatore,
  type DeviceClient,
  type Istantanea,
  type PuntoStorico,
  type RichiestaStorico,
} from './DeviceClient';
import { generaStorico, type ProfiloCanale } from './storico';

export interface ScenarioIniziale {
  raggiungibile?: boolean;
  sondeScollegate?: string[];
  sorgentiIrraggiungibili?: string[];
  allarmeDeltaT?: boolean;
  velocita?: number;
}

export interface OpzioniSimulatore {
  impianto: Impianto;
  scenario?: ScenarioIniziale;
  /** Periodo di pubblicazione delle letture. La specifica chiede ≤ 30 s; la demo usa 5 s. */
  intervalloLettureMs?: number;
  intervalloTickMs?: number;
  orologio?: () => number;
  /** Credenziali dell'area tecnica. Esistono solo nel simulatore: in produzione non ci sono credenziali predefinite (RNF-21). */
  credenziali?: Record<string, { password: string; ruolo: 'tecnico' | 'admin'; operatore: string }>;
  /** Registro sessioni condiviso fra gli impianti di uno stesso parco (la sessione la emette il cloud, non la singola centralina). */
  registroSessioni?: Map<string, SessioneTecnica>;
}

export interface ScenarioSimulatore {
  setRaggiungibile(valore: boolean): void;
  setSondaScollegata(canale: string, scollegata: boolean): void;
  setSorgenteRaggiungibile(sorgenteId: string, raggiungibile: boolean): void;
  setVelocita(valore: number): void;
  stato(): { raggiungibile: boolean; sondeScollegate: string[]; sorgentiIrraggiungibili: string[]; velocita: number };
}

type Timer = ReturnType<typeof setTimeout>;

// Credenziali di sola dimostrazione: esistono UNICAMENTE nel simulatore.
// La produzione non deve avere alcuna credenziale predefinita (RNF-21):
// nessun client reale (LocalHttpClient, CloudClient) può ereditarle.
const CREDENZIALI_DEMO: NonNullable<OpzioniSimulatore['credenziali']> = {
  tecnico: { password: 'emacomfort', ruolo: 'tecnico', operatore: 'Mario Rossi' },
  admin: { password: 'emacomfort', ruolo: 'admin', operatore: 'Amministratore' },
};

function luceDelGiorno(ore: number): number {
  return Math.max(0, Math.sin(((ore - 6) / 14) * Math.PI));
}

function arrotonda(v: number, cifre = 1): number {
  const f = 10 ** cifre;
  return Math.round(v * f) / f;
}

function avvicina(attuale: number, obiettivo: number, passo: number): number {
  if (attuale < obiettivo) return Math.min(obiettivo, attuale + passo);
  if (attuale > obiettivo) return Math.max(obiettivo, attuale - passo);
  return attuale;
}

function contiAdc(temperatura: number): number {
  const r = 10 * Math.exp(3950 * (1 / (temperatura + 273.15) - 1 / 298.15));
  return Math.round((4095 * 10) / (10 + r));
}

/**
 * Impianto simulato con dinamica termica plausibile (specifica §9.5).
 *
 * La REGOLAZIONE vive qui perché questo oggetto rappresenta la centralina, non
 * l'app: l'app invia intenzioni, la centralina decide (§9.1). La dinamica è
 * compressa per essere visibile in una dimostrazione di pochi minuti.
 *
 * ⚠️ La strategia di regolazione è quella ASSUNTA nella soluzione end-to-end
 * §5, in attesa dell'algoritmo Arduino del committente.
 */
export class SimulatorClient implements DeviceClient {
  readonly impiantoId: string;
  readonly tecnico: AreaTecnicaClient;
  readonly scenario: ScenarioSimulatore;

  private impianto: Impianto;
  private readonly orologio: () => number;
  private readonly intervalloLettureMs: number;
  private readonly intervalloTickMs: number;
  private readonly credenziali: NonNullable<OpzioniSimulatore['credenziali']>;

  private velocita = 1;
  private raggiungibile = true;
  private readonly sondeScollegate = new Set<string>();
  private readonly sorgentiGiu = new Set<string>();
  private forzaDeltaTBasso = false;

  private readonly ascoltatori = new Set<Ascoltatore>();
  private readonly valori = new Map<string, number>();
  private readonly attuatori = new Map<string, StatoAttuatore>();
  private readonly forzature = new Map<string, { fino: number; timer: Timer }>();
  private readonly zoneInDomanda = new Set<string>();
  private readonly generatoriAttivi = new Set<string>();
  private sanitario: StatoSanitario;
  private vmcAttiva = false;
  private energia: Energia | null = null;
  private ultimeLetture: Record<string, Lettura> = {};
  private ultimaPubblicazione: number | null = null;
  private readonly ultimoAggiornamentoSorgente = new Map<string, number | null>();
  private readonly allarmi: Allarme[] = [];
  private readonly eventi: EventoStato[] = [];
  private readonly registro: VoceRegistro[] = [];
  private readonly programmi = new Map<string, ProgrammaOrario>();
  private readonly sessioni: Map<string, SessioneTecnica>;
  private ota: StatoOta;
  private otaTimer: Timer | null = null;

  private tempoSim: number;
  private domandaInsoddisfattaDa: number | null = null;
  private deltaTBassoDa: number | null = null;
  private rugiadaSegnalata = false;
  private contatore = 0;
  private timerTick: Timer | null = null;
  private timerLetture: Timer | null = null;

  constructor(opzioni: OpzioniSimulatore) {
    this.impianto = structuredClone(opzioni.impianto);
    this.impiantoId = this.impianto.id;
    this.orologio = opzioni.orologio ?? (() => Date.now());
    this.intervalloLettureMs = opzioni.intervalloLettureMs ?? 5_000;
    this.intervalloTickMs = opzioni.intervalloTickMs ?? 1_000;
    this.credenziali = opzioni.credenziali ?? CREDENZIALI_DEMO;
    this.sessioni = opzioni.registroSessioni ?? new Map();
    this.tempoSim = this.orologio();
    this.sanitario = { setpoint: this.impianto.sanitario.setpoint, boostFinoA: null, inCarica: false };
    this.ota = {
      versioneCorrente: this.impianto.centralina.firmware,
      versioneDisponibile: this.impianto.centralina.tipo === 'emacomfort' ? '0.9.3' : null,
      versionePrecedente: null,
      fase: 'inattivo',
      avanzamentoPct: 0,
      firmata: true,
    };

    const sc = opzioni.scenario ?? {};
    this.raggiungibile = sc.raggiungibile ?? true;
    sc.sondeScollegate?.forEach((c) => this.sondeScollegate.add(c));
    sc.sorgentiIrraggiungibili?.forEach((s) => this.sorgentiGiu.add(s));
    this.forzaDeltaTBasso = sc.allarmeDeltaT ?? false;
    this.velocita = sc.velocita ?? 1;

    this.inizializzaValori();
    this.impianto.sorgenti.forEach((s) => this.ultimoAggiornamentoSorgente.set(s.id, null));
    this.tecnico = this.creaAreaTecnica();
    this.scenario = this.creaScenario();
  }

  // ───────────────────────────── ciclo di vita ─────────────────────────────

  avvia(): void {
    if (this.timerTick) return;
    this.timerTick = setInterval(() => this.tick(), this.intervalloTickMs);
    this.timerLetture = setInterval(() => this.pubblicaLetture(), this.intervalloLettureMs);
    this.tick();
    this.pubblicaLetture();
  }

  ferma(): void {
    if (this.timerTick) clearInterval(this.timerTick);
    if (this.timerLetture) clearInterval(this.timerLetture);
    if (this.otaTimer) clearTimeout(this.otaTimer);
    this.forzature.forEach((f) => clearTimeout(f.timer));
    this.timerTick = null;
    this.timerLetture = null;
  }

  // ───────────────────────────── DeviceClient ─────────────────────────────

  async getImpianto(): Promise<Impianto> {
    return structuredClone(this.impianto);
  }

  getIstantanea(): Istantanea {
    const sorgenti: Record<string, StatoSorgente> = {};
    this.impianto.sorgenti.forEach((s) => {
      sorgenti[s.id] = {
        id: s.id,
        raggiungibile: this.raggiungibile && !this.sorgentiGiu.has(s.id),
        ultimoAggiornamento: this.ultimoAggiornamentoSorgente.get(s.id) ?? null,
      };
    });
    const energia: Record<string, Energia> = {};
    if (this.energia && this.impianto.energia.sorgenteId) energia[this.impianto.energia.sorgenteId] = this.energia;
    return {
      ts: this.ultimaPubblicazione ?? 0,
      letture: { ...this.ultimeLetture },
      attuatori: Object.fromEntries(this.attuatori),
      stato: this.statoCorrente(),
      energia,
      sorgenti,
      allarmi: this.allarmi.map((a) => ({ ...a })),
    };
  }

  sottoscrivi(ascoltatore: Ascoltatore): () => void {
    this.ascoltatori.add(ascoltatore);
    return () => this.ascoltatori.delete(ascoltatore);
  }

  inviaComando(comando: Comando): Promise<RispostaComando> {
    return new Promise((resolve) => {
      const fallisci = (motivo: MotivoFallimento, ritardo: number) => {
        setTimeout(() => {
          this.emetti({ tipo: 'esito_comando', ts: this.orologio(), idComando: comando.idComando, esito: 'fallito', motivo });
          resolve({ idComando: comando.idComando, esito: 'fallito', motivo });
        }, ritardo);
      };
      if (!this.raggiungibile) {
        fallisci('centralina_irraggiungibile', TIMEOUT_COMANDO_MS);
        return;
      }
      const motivo = this.validaComando(comando);
      if (motivo) {
        fallisci(motivo, 150);
        return;
      }
      setTimeout(() => {
        this.emetti({ tipo: 'esito_comando', ts: this.orologio(), idComando: comando.idComando, esito: 'accettato' });
        setTimeout(() => {
          if (!this.raggiungibile) {
            this.emetti({ tipo: 'esito_comando', ts: this.orologio(), idComando: comando.idComando, esito: 'fallito', motivo: 'timeout' });
            resolve({ idComando: comando.idComando, esito: 'fallito', motivo: 'timeout' });
            return;
          }
          this.applicaComando(comando);
          this.emetti({ tipo: 'esito_comando', ts: this.orologio(), idComando: comando.idComando, esito: 'applicato' });
          this.emettiStato();
          resolve({ idComando: comando.idComando, esito: 'applicato' });
        }, 900);
      }, 250);
    });
  }

  async getStorico(richiesta: RichiestaStorico): Promise<PuntoStorico[]> {
    return generaStorico(richiesta, this.profiliStorico());
  }

  async getEventi(): Promise<EventoStato[]> {
    return [...this.eventi].reverse();
  }

  async getAllarmi(): Promise<Allarme[]> {
    return this.allarmi.map((a) => ({ ...a })).sort((a, b) => b.ts - a.ts);
  }

  async riconosciAllarme(id: string, chi: string): Promise<void> {
    const a = this.allarmi.find((x) => x.id === id);
    if (!a || a.stato !== 'aperto') return;
    a.stato = 'riconosciuto';
    a.riconosciutoDa = chi;
    a.riconosciutoIl = this.orologio();
    this.emetti({ tipo: 'allarme', ts: a.riconosciutoIl, allarme: { ...a } });
  }

  async getProgramma(zonaId: string): Promise<ProgrammaOrario> {
    const esistente = this.programmi.get(zonaId);
    if (esistente) return structuredClone(esistente);
    const zona = this.impianto.zone.find((z) => z.id === zonaId);
    if (!zona) throw new ErroreRichiesta('zona_inesistente');
    const predefinito = zona.ordinamento <= 2 ? programmaTipico(zonaId, zona.setpoint, zona.setpoint - 2) : programmaVuoto(zonaId);
    this.programmi.set(zonaId, predefinito);
    return structuredClone(predefinito);
  }

  /** Sintesi per il parco impianti (T2). */
  sintesi(): ImpiantoSintesi {
    const aperti = this.allarmi.filter((a) => a.stato !== 'chiuso');
    const ordine: SeveritaAllarme[] = ['bassa', 'media', 'alta', 'critica'];
    const massimo = aperti.reduce<SeveritaAllarme | null>((m, a) => (m === null || ordine.indexOf(a.severita) > ordine.indexOf(m) ? a.severita : m), null);
    const connessione = !this.raggiungibile ? 'irraggiungibile' : this.sorgentiGiu.size > 0 ? 'degradato' : 'connesso';
    return {
      id: this.impianto.id,
      tenantId: this.impianto.tenantId,
      nome: this.impianto.nome,
      citta: this.impianto.indirizzo.citta,
      cliente: this.impianto.cliente,
      tipoCentralina: this.impianto.centralina.tipo,
      connessione,
      ultimoContatto: this.ultimaPubblicazione,
      allarmiAperti: aperti.length,
      allarmeMassimo: massimo,
      zone: this.impianto.zone.length,
      generatori: this.impianto.generatori.length,
      firmware: this.ota.versioneCorrente,
    };
  }

  // ───────────────────────────── scenario (solo simulatore) ─────────────────────────────

  private creaScenario(): ScenarioSimulatore {
    return {
      setRaggiungibile: (valore) => {
        if (this.raggiungibile === valore) return;
        this.raggiungibile = valore;
        if (valore) {
          this.ultimaPubblicazione = null;
          this.pubblicaLetture();
          this.emettiStato();
        }
        this.registraEvento('connessione', valore ? 'centralina_raggiungibile' : 'centralina_irraggiungibile');
      },
      setSondaScollegata: (canale, scollegata) => {
        if (scollegata) this.sondeScollegate.add(canale);
        else this.sondeScollegate.delete(canale);
        this.pubblicaLetture();
      },
      setSorgenteRaggiungibile: (sorgenteId, raggiungibile) => {
        if (raggiungibile) this.sorgentiGiu.delete(sorgenteId);
        else this.sorgentiGiu.add(sorgenteId);
        this.pubblicaLetture();
      },
      setVelocita: (valore) => {
        this.velocita = Math.max(0.1, Math.min(60, valore));
      },
      stato: () => ({
        raggiungibile: this.raggiungibile,
        sondeScollegate: [...this.sondeScollegate],
        sorgentiIrraggiungibili: [...this.sorgentiGiu],
        velocita: this.velocita,
      }),
    };
  }

  // ───────────────────────────── stato interno ─────────────────────────────

  private inizializzaValori(): void {
    const ore = new Date(this.orologio()).getHours() + new Date(this.orologio()).getMinutes() / 60;
    this.impianto.canali.forEach((c) => {
      if (c.direzione === 'OUTPUT') {
        this.attuatori.set(c.id, { canale: c.id, ts: this.orologio(), stato: 'spento', modulazione: c.grandezza === 'MODULAZIONE' ? 0 : undefined });
        return;
      }
      const f = this.funzioneDi(c);
      const zona = this.zonaDellaSonda(c.id);
      let v = 22;
      if (zona) v = zona.setpoint + (zona.ordinamento % 2 === 0 ? -0.7 : 0.4);
      else if (f === 'MANDATA_COLLETTORE') v = 36.5;
      else if (f === 'RITORNO_COLLETTORE') v = 30.1;
      else if (f === 'ACCUMULO_SANITARIO') v = this.impianto.sanitario.setpoint - 1.5;
      else if (f === 'MANDATA_GENERATORE') v = 24;
      else if (f === 'RITORNO_GENERATORE') v = 23;
      else if (f === 'PUFFER_ALTO') v = 44;
      else if (f === 'PUFFER_BASSO') v = 36;
      else if (f === 'TEMPERATURA_ESTERNA') v = 10.5 + 3 * Math.sin(((ore - 9) / 24) * Math.PI * 2);
      else if (f === 'UMIDITA_INTERNA') v = 45;
      else if (f === 'LUMINOSITA') v = luceDelGiorno(ore) * 100;
      this.valori.set(c.id, v);
    });
    if (this.impianto.energia.presente) {
      this.energia = { ts: this.orologio(), produzioneW: 0, consumoW: 700, batteriaPct: this.impianto.energia.capacitaBatteriaKwh ? 55 : 0, reteW: 700, batteriaW: 0, q: 'ok' };
    }
    if (this.forzaDeltaTBasso) this.apriAllarme('DELTA_T_ANOMALO', 'media', 'collettore');
  }

  private funzioneDi(c: Canale): FunzioneImpianto | null {
    return c.assegnazione.tipo === 'funzione' ? c.assegnazione.funzione : null;
  }

  private canalePer(funzione: FunzioneImpianto, generatoreId?: string): Canale | undefined {
    return this.impianto.canali.find(
      (c) => c.assegnazione.tipo === 'funzione' && c.assegnazione.funzione === funzione && (generatoreId === undefined || c.assegnazione.generatoreId === generatoreId),
    );
  }

  private zonaDellaSonda(canaleId: string): Zona | undefined {
    return this.impianto.zone.find((z) => z.canaleSonda === canaleId);
  }

  private valore(funzione: FunzioneImpianto, generatoreId?: string): number | undefined {
    const c = this.canalePer(funzione, generatoreId);
    return c ? this.valori.get(c.id) : undefined;
  }

  private setValore(funzione: FunzioneImpianto, v: number, generatoreId?: string): void {
    const c = this.canalePer(funzione, generatoreId);
    if (c) this.valori.set(c.id, v);
  }

  private regime(): 'caldo' | 'freddo' | 'nessuno' {
    const m = this.impianto.modalita;
    if (m === 'RISCALDAMENTO') return 'caldo';
    if (m === 'RAFFRESCAMENTO') return 'freddo';
    if (m === 'SPENTO') return 'nessuno';
    const esterna = this.valore('TEMPERATURA_ESTERNA') ?? 15;
    const s = this.impianto.parametri.commutazioneStagionale;
    if (esterna < s.sogliaRiscaldamento) return 'caldo';
    if (esterna > s.sogliaRaffrescamento) return 'freddo';
    return 'nessuno';
  }

  private setpointEffettivo(zona: Zona): number {
    const programma = this.programmi.get(zona.id);
    if (programma?.attivo) {
      const fascia = fasciaAttiva(programma, new Date(this.orologio()));
      if (fascia) return fascia.setpoint;
    }
    return zona.setpoint;
  }

  private statoCorrente(): StatoImpianto {
    return {
      ts: this.orologio(),
      modalita: this.impianto.modalita,
      zone: structuredClone(this.impianto.zone),
      sanitario: { ...this.sanitario },
      vmc: { modalita: this.impianto.vmc.modalita, attiva: this.vmcAttiva, modulazione: this.attuatori.get(this.canalePer('VMC')?.id ?? '')?.modulazione },
      zoneInDomanda: [...this.zoneInDomanda],
    };
  }

  // ───────────────────────────── regolazione + fisica ─────────────────────────────

  private tick(): void {
    const dt = (this.intervalloTickMs / 1000) * this.velocita;
    this.tempoSim += dt * 1000;
    this.regola();
    this.fisica(dt);
    this.controllaAllarmi();
  }

  private regola(): void {
    const p = this.impianto.parametri;
    const regime = this.regime();
    const adesso = this.orologio();

    // Sanitario: priorità con tetto (soluzione end-to-end §5.3)
    if (this.sanitario.boostFinoA !== null && this.sanitario.boostFinoA < adesso) {
      this.sanitario.boostFinoA = null;
      this.registraEvento('sanitario', 'boost_terminato');
    }
    const acs = this.valore('ACCUMULO_SANITARIO');
    if (acs !== undefined && this.impianto.sanitario.presente) {
      const boost = this.sanitario.boostFinoA !== null;
      const obiettivo = this.sanitario.setpoint + (boost ? 2 : 0);
      if (!this.sanitario.inCarica && (acs < this.sanitario.setpoint - p.sanitario.isteresi || (boost && acs < obiettivo))) {
        this.sanitario.inCarica = true;
        this.registraEvento('sanitario', 'carica_avviata');
      } else if (this.sanitario.inCarica && acs >= obiettivo) {
        this.sanitario.inCarica = false;
        this.registraEvento('sanitario', 'carica_completata');
      }
    }

    // Zone: isteresi (§5.2)
    this.impianto.zone.forEach((z) => {
      const t = this.valori.get(z.canaleSonda);
      const sp = this.setpointEffettivo(z);
      const h = p.isteresi.zona / 2;
      const era = this.zoneInDomanda.has(z.id);
      let ora = era;
      if (!z.abilitata || regime === 'nessuno' || t === undefined || this.sondeScollegate.has(z.canaleSonda)) {
        ora = false;
      } else if (regime === 'caldo') {
        ora = era ? t < sp + h : t < sp - h;
      } else {
        ora = era ? t > sp - h : t > sp + h;
      }
      if (ora) this.zoneInDomanda.add(z.id);
      else this.zoneInDomanda.delete(z.id);
      z.canaliAttuatore.forEach((c) => this.impostaAttuatore(c, ora ? 'attivo' : 'spento'));
    });

    const domanda = this.zoneInDomanda.size > 0 || this.sanitario.inCarica;
    const nZone = Math.max(1, this.impianto.zone.filter((z) => z.abilitata).length);

    // Circolatore
    const circ = this.canalePer('CIRCOLATORE');
    if (circ) {
      const mod = p.circolatore.modalita === 'MODULANTE'
        ? Math.round(p.circolatore.portataMinimaPct + (100 - p.circolatore.portataMinimaPct) * (this.zoneInDomanda.size / nZone))
        : 100;
      this.impostaAttuatore(circ.id, domanda ? 'attivo' : 'spento', circ.grandezza === 'MODULAZIONE' ? (domanda ? mod : 0) : undefined);
    }

    // Valvola deviatrice
    const dev = this.canalePer('VALVOLA_DEVIATRICE');
    if (dev) this.impostaAttuatore(dev.id, this.sanitario.inCarica ? 'attivo' : 'spento');

    // Cascata generatori (§5.3)
    const generatori = [...this.impianto.generatori].sort((a, b) => a.priorita - b.priorita);
    const mandata = this.valore('MANDATA_COLLETTORE') ?? 30;
    const obiettivoMandata = this.obiettivoMandata(regime);
    if (domanda && regime === 'caldo' && mandata < obiettivoMandata - 3) {
      this.domandaInsoddisfattaDa ??= this.tempoSim;
    } else {
      this.domandaInsoddisfattaDa = null;
    }
    const secondarioDovuto = this.domandaInsoddisfattaDa !== null && this.tempoSim - this.domandaInsoddisfattaDa > p.cascata.ritardoSecondarioMin * 60_000;
    generatori.forEach((g, i) => {
      const attivo = domanda && (i === 0 || (i === 1 && secondarioDovuto));
      const era = this.generatoriAttivi.has(g.id);
      if (attivo && !era) this.registraEvento('generatore', `acceso:${g.nome}`);
      if (!attivo && era) this.registraEvento('generatore', `spento:${g.nome}`);
      if (attivo) this.generatoriAttivi.add(g.id);
      else this.generatoriAttivi.delete(g.id);
      if (g.canaleComando) {
        const c = this.impianto.canali.find((x) => x.id === g.canaleComando);
        const modulazione = c?.grandezza === 'MODULAZIONE' ? this.modulazionePin12(attivo, mandata, obiettivoMandata) : undefined;
        this.impostaAttuatore(g.canaleComando, attivo ? 'attivo' : 'spento', modulazione);
      }
    });
    const pin12 = this.canalePer('LOGICA_GENERATORE');
    if (pin12) {
      const primario = generatori[0];
      const attivo = primario ? this.generatoriAttivi.has(primario.id) : false;
      this.impostaAttuatore(pin12.id, attivo ? 'attivo' : 'spento', pin12.grandezza === 'MODULAZIONE' ? this.modulazionePin12(attivo, mandata, obiettivoMandata) : undefined);
    }

    // VMC
    const vmc = this.canalePer('VMC');
    if (vmc && this.impianto.vmc.presente) {
      const umidita = this.valore('UMIDITA_INTERNA') ?? 45;
      const modalita = this.impianto.vmc.modalita;
      const attiva = modalita === 'ACCESA' || (modalita === 'AUTO' && umidita > this.impianto.vmc.sogliaUmidita);
      if (attiva !== this.vmcAttiva) this.registraEvento('vmc', attiva ? 'attivata' : 'disattivata');
      this.vmcAttiva = attiva;
      this.impostaAttuatore(vmc.id, attiva ? 'attivo' : 'spento', vmc.grandezza === 'MODULAZIONE' ? (attiva ? (modalita === 'AUTO' ? 60 : 100) : 0) : undefined);
    }
  }

  private modulazionePin12(attivo: boolean, mandata: number, obiettivo: number): number {
    if (!attivo) return 0;
    const p = this.impianto.parametri;
    switch (p.comportamentoPin12) {
      case 2: {
        const nZone = Math.max(1, this.impianto.zone.filter((z) => z.abilitata).length);
        return Math.round(p.circolatore.portataMinimaPct + (100 - p.circolatore.portataMinimaPct) * (this.zoneInDomanda.size / nZone));
      }
      case 3:
        return Math.round(Math.max(20, Math.min(100, (obiettivo - mandata) * 20 + 40)));
      default:
        return 100;
    }
  }

  private obiettivoMandata(regime: 'caldo' | 'freddo' | 'nessuno'): number {
    const p = this.impianto.parametri;
    const esterna = this.valore('TEMPERATURA_ESTERNA') ?? 10;
    const zone = this.impianto.zone.filter((z) => z.abilitata);
    const spMedia = zone.length ? zone.reduce((s, z) => s + this.setpointEffettivo(z), 0) / zone.length : 21;
    if (regime === 'caldo') return mandataDaCurva(p.curvaClimatica, spMedia, esterna);
    if (regime === 'freddo') {
      const umidita = this.valore('UMIDITA_INTERNA') ?? 50;
      const rugiada = puntoDiRugiada(spMedia, umidita);
      const minimo = rugiada + p.protezioni.margineRugiada;
      const obiettivo = 17;
      if (obiettivo < minimo) {
        if (!this.rugiadaSegnalata) {
          this.rugiadaSegnalata = true;
          this.registraEvento('protezione', `punto_di_rugiada:${arrotonda(minimo)}`);
          this.apriAllarme('PUNTO_DI_RUGIADA', 'alta', 'mandata');
        }
        return minimo;
      }
      this.rugiadaSegnalata = false;
      this.chiudiAllarme('PUNTO_DI_RUGIADA', 'mandata');
      return obiettivo;
    }
    return 22;
  }

  private fisica(dt: number): void {
    const regime = this.regime();
    const p = this.impianto.parametri;
    const adesso = this.orologio();
    const ore = new Date(adesso).getHours() + new Date(adesso).getMinutes() / 60;
    const t = adesso / 1000;

    // Esterna, umidità, luminosità
    const esterna = 10.5 + 3 * Math.sin(((ore - 9) / 24) * Math.PI * 2) + 0.3 * Math.sin(t / 900);
    this.setValore('TEMPERATURA_ESTERNA', esterna);
    const umidita = 45 + 4 * Math.sin(t / 1800) + (this.vmcAttiva ? -3 : 0);
    this.setValore('UMIDITA_INTERNA', Math.max(20, Math.min(90, umidita)));
    this.setValore('LUMINOSITA', luceDelGiorno(ore) * 100 * (0.9 + 0.1 * Math.sin(t / 300)));

    const circolatoreOn = this.attuatori.get(this.canalePer('CIRCOLATORE')?.id ?? '')?.stato === 'attivo';
    const generatoreOn = this.generatoriAttivi.size > 0;
    const obiettivo = this.obiettivoMandata(regime);

    // Zone
    this.impianto.zone.forEach((z) => {
      const attuale = this.valori.get(z.canaleSonda);
      if (attuale === undefined) return;
      const valvola = z.canaliAttuatore.some((c) => this.attuatori.get(c)?.stato === 'attivo');
      const flusso = valvola && circolatoreOn && generatoreOn && !this.sanitario.inCarica;
      let delta = (esterna - attuale) * 0.00025;
      if (flusso && regime === 'caldo') delta += 0.008;
      if (flusso && regime === 'freddo') delta -= 0.008;
      delta += 0.0002 * Math.sin(t / 600 + z.ordinamento);
      this.valori.set(z.canaleSonda, attuale + delta * dt);
    });

    // Collettore
    const mandata = this.valore('MANDATA_COLLETTORE');
    if (mandata !== undefined) {
      const target = generatoreOn && circolatoreOn ? obiettivo : 22;
      this.setValore('MANDATA_COLLETTORE', avvicina(mandata, target, (generatoreOn ? 0.04 : 0.006) * dt));
      const ritorno = this.valore('RITORNO_COLLETTORE');
      if (ritorno !== undefined) {
        const nuovaMandata = this.valore('MANDATA_COLLETTORE') ?? mandata;
        const deltaT = this.forzaDeltaTBasso ? 1 : circolatoreOn ? p.deltaT.nominale * (0.9 + 0.1 * Math.sin(t / 200)) : 0.5;
        this.setValore('RITORNO_COLLETTORE', avvicina(ritorno, nuovaMandata - deltaT, 0.05 * dt));
      }
    }

    // Generatori
    this.impianto.generatori.forEach((g) => {
      const attivo = this.generatoriAttivi.has(g.id);
      const m = this.valori.get(g.canaleMandata);
      const r = this.valori.get(g.canaleRitorno);
      const base = this.valore('MANDATA_COLLETTORE') ?? 30;
      if (m !== undefined) this.valori.set(g.canaleMandata, avvicina(m, attivo ? base + 3 : 22, (attivo ? 0.05 : 0.006) * dt));
      if (r !== undefined) this.valori.set(g.canaleRitorno, avvicina(r, attivo ? (this.valori.get(g.canaleMandata) ?? base) - 6 : 21.5, 0.05 * dt));
    });

    // Puffer (solo dove esistono le sonde)
    const pAlto = this.valore('PUFFER_ALTO');
    if (pAlto !== undefined) {
      this.setValore('PUFFER_ALTO', avvicina(pAlto, generatoreOn ? obiettivo + 4 : 38, (generatoreOn ? 0.02 : 0.003) * dt));
      const pBasso = this.valore('PUFFER_BASSO');
      if (pBasso !== undefined) this.setValore('PUFFER_BASSO', avvicina(pBasso, (this.valore('PUFFER_ALTO') ?? pAlto) - 8, 0.02 * dt));
    }

    // Sanitario
    const acs = this.valore('ACCUMULO_SANITARIO');
    if (acs !== undefined) {
      this.setValore('ACCUMULO_SANITARIO', this.sanitario.inCarica ? acs + 0.015 * dt : acs - 0.0012 * dt);
    }

    // Energia (connettore fotovoltaico)
    if (this.energia && this.impianto.energia.presente) {
      const picco = (this.impianto.energia.potenzaPiccoKw ?? 6) * 1000;
      const produzione = picco * luceDelGiorno(ore) ** 1.3 * (0.85 + 0.15 * Math.sin(t / 600));
      const pdcOn = [...this.generatoriAttivi].some((id) => this.impianto.generatori.find((g) => g.id === id)?.tipo === 'POMPA_DI_CALORE');
      const consumo = 650 + (pdcOn ? 2400 : 0) + 150 * Math.sin(t / 300) + (this.vmcAttiva ? 80 : 0);
      const surplus = produzione - consumo;
      const cap = (this.impianto.energia.capacitaBatteriaKwh ?? 0) * 1000;
      let batteriaW = 0;
      let pct = this.energia.batteriaPct;
      if (cap > 0) {
        if (surplus > 0 && pct < 100) batteriaW = Math.min(surplus, 3000);
        else if (surplus < 0 && pct > 10) batteriaW = Math.max(surplus, -3000);
        pct = Math.max(0, Math.min(100, pct + ((batteriaW * dt) / 3600 / cap) * 100));
      }
      this.energia = {
        ts: adesso,
        produzioneW: Math.round(produzione),
        consumoW: Math.round(consumo),
        batteriaPct: Math.round(pct),
        batteriaW: Math.round(batteriaW),
        reteW: Math.round(consumo - produzione + batteriaW),
        q: 'ok',
      };
    }

    // Forzature scadute (rientro imposto dalla centralina, RF-35)
    this.forzature.forEach((f, canale) => {
      if (f.fino <= adesso) this.rientraForzatura(canale, 'timeout');
    });
  }

  private impostaAttuatore(canale: string, stato: 'attivo' | 'spento', modulazione?: number): void {
    if (this.forzature.has(canale)) return;
    const attuale = this.attuatori.get(canale);
    if (!attuale) return;
    const mod = modulazione ?? attuale.modulazione;
    if (attuale.stato === stato && attuale.modulazione === mod) return;
    const nuovo: StatoAttuatore = { canale, ts: this.orologio(), stato, modulazione: mod };
    this.attuatori.set(canale, nuovo);
    if (this.raggiungibile) this.emetti({ tipo: 'attuatore', ts: nuovo.ts, stato: nuovo });
  }

  private controllaAllarmi(): void {
    const p = this.impianto.parametri;
    this.impianto.canali
      .filter((c) => c.direzione === 'INPUT')
      .forEach((c) => {
        if (this.sondeScollegate.has(c.id)) this.apriAllarme('SONDA_SCOLLEGATA', 'alta', c.id);
        else this.chiudiAllarme('SONDA_SCOLLEGATA', c.id);
      });
    const circolatoreOn = this.attuatori.get(this.canalePer('CIRCOLATORE')?.id ?? '')?.stato === 'attivo';
    const m = this.valore('MANDATA_COLLETTORE');
    const r = this.valore('RITORNO_COLLETTORE');
    if (m !== undefined && r !== undefined) {
      if (circolatoreOn && this.generatoriAttivi.size > 0 && m - r < p.deltaT.sogliaAllarme) {
        this.deltaTBassoDa ??= this.tempoSim;
        if (this.tempoSim - this.deltaTBassoDa > p.deltaT.minutiPrimaDellAllarme * 60_000) this.apriAllarme('DELTA_T_ANOMALO', 'media', 'collettore');
      } else {
        this.deltaTBassoDa = null;
        if (!this.forzaDeltaTBasso) this.chiudiAllarme('DELTA_T_ANOMALO', 'collettore');
      }
      if (m > p.curvaClimatica.mandataMax + p.protezioni.sovratemperaturaDelta) this.apriAllarme('SOVRATEMPERATURA', 'critica', 'mandata');
      else this.chiudiAllarme('SOVRATEMPERATURA', 'mandata');
    }
    this.impianto.sorgenti.forEach((s) => {
      if (this.sorgentiGiu.has(s.id)) this.apriAllarme('SORGENTE_IRRAGGIUNGIBILE', 'media', s.id);
      else this.chiudiAllarme('SORGENTE_IRRAGGIUNGIBILE', s.id);
    });
  }

  private apriAllarme(codice: CodiceAllarme, severita: SeveritaAllarme, oggetto: string): void {
    const aperto = this.allarmi.find((a) => a.codice === codice && a.oggetto === oggetto && a.stato !== 'chiuso');
    if (aperto) return;
    const a: Allarme = { id: this.nuovoId('al'), ts: this.orologio(), codice, severita, stato: 'aperto', oggetto };
    this.allarmi.push(a);
    this.registraEvento('allarme', `${codice}:${oggetto}`);
    this.emetti({ tipo: 'allarme', ts: a.ts, allarme: { ...a } });
  }

  private chiudiAllarme(codice: CodiceAllarme, oggetto: string): void {
    const aperto = this.allarmi.find((a) => a.codice === codice && a.oggetto === oggetto && a.stato !== 'chiuso');
    if (!aperto) return;
    aperto.stato = 'chiuso';
    aperto.chiusoIl = this.orologio();
    this.emetti({ tipo: 'allarme', ts: aperto.chiusoIl, allarme: { ...aperto } });
  }

  private pubblicaLetture(): void {
    if (!this.raggiungibile) return;
    const ts = this.orologio();
    const valori: Record<string, Lettura> = {};
    this.impianto.canali
      .filter((c) => c.direzione === 'INPUT')
      .forEach((c) => {
        const modulo = this.impianto.moduli.find((m) => m.id === c.moduloId);
        if (modulo?.sorgenteId && this.sorgentiGiu.has(modulo.sorgenteId)) return;
        if (this.sondeScollegate.has(c.id)) {
          valori[c.id] = { canale: c.id, ts, v: null, u: c.unita, q: 'sonda_scollegata' };
          return;
        }
        const grezzo = this.valori.get(c.id);
        if (grezzo === undefined) {
          valori[c.id] = { canale: c.id, ts, v: null, u: c.unita, q: 'mancante' };
          return;
        }
        valori[c.id] = { canale: c.id, ts, v: arrotonda(applicaTaratura(grezzo, c.taratura)), u: c.unita, q: 'ok' };
      });
    this.ultimeLetture = { ...this.ultimeLetture, ...valori };
    this.ultimaPubblicazione = ts;
    this.impianto.sorgenti.forEach((s) => {
      const su = !this.sorgentiGiu.has(s.id);
      if (su) this.ultimoAggiornamentoSorgente.set(s.id, ts);
      this.emetti({ tipo: 'sorgente', ts, sorgente: { id: s.id, raggiungibile: su, ultimoAggiornamento: this.ultimoAggiornamentoSorgente.get(s.id) ?? null } });
    });
    this.emetti({ tipo: 'letture', ts, valori });
    if (this.energia && this.impianto.energia.sorgenteId && !this.sorgentiGiu.has(this.impianto.energia.sorgenteId)) {
      this.emetti({ tipo: 'energia', ts, sorgenteId: this.impianto.energia.sorgenteId, energia: { ...this.energia, ts } });
    }
  }

  private emettiStato(): void {
    if (!this.raggiungibile) return;
    this.emetti({ tipo: 'stato', ts: this.orologio(), stato: this.statoCorrente() });
  }

  private emetti(evento: EventoDispositivo): void {
    this.ascoltatori.forEach((a) => a(evento));
  }

  private registraEvento(tipo: string, descrizione: string): void {
    const e: EventoStato = { ts: this.orologio(), tipo, descrizione };
    this.eventi.push(e);
    if (this.eventi.length > 300) this.eventi.shift();
    this.emetti({ tipo: 'evento', ts: e.ts, evento: e });
  }

  private nuovoId(prefisso: string): string {
    this.contatore += 1;
    return `${prefisso}-${this.contatore.toString(36)}`;
  }

  // ───────────────────────────── comandi ─────────────────────────────

  private validaComando(comando: Comando): MotivoFallimento | null {
    switch (comando.azione) {
      case 'set_setpoint': {
        const z = this.impianto.zone.find((x) => x.id === comando.zonaId);
        if (!z) return 'zona_inesistente';
        if (!validaSetpoint(comando.valore, z.limiti).ok) return 'fuori_limiti';
        return null;
      }
      case 'set_zona_abilitata':
        return this.impianto.zone.some((x) => x.id === comando.zonaId) ? null : 'zona_inesistente';
      case 'set_setpoint_globale':
        return this.impianto.zone.filter((z) => !z.sovrascritta).every((z) => validaSetpoint(comando.valore, z.limiti).ok) ? null : 'fuori_limiti';
      case 'set_sanitario':
        if (comando.setpoint !== undefined && (comando.setpoint < 35 || comando.setpoint > 65)) return 'fuori_limiti';
        return null;
      case 'set_programma':
        return this.impianto.zone.some((x) => x.id === comando.zonaId) ? null : 'zona_inesistente';
      default:
        return null;
    }
  }

  private applicaComando(comando: Comando): void {
    switch (comando.azione) {
      case 'set_setpoint': {
        const z = this.impianto.zone.find((x) => x.id === comando.zonaId)!;
        z.setpoint = comando.valore;
        z.sovrascritta = true;
        break;
      }
      case 'set_zona_abilitata': {
        const z = this.impianto.zone.find((x) => x.id === comando.zonaId)!;
        z.abilitata = comando.abilitata;
        break;
      }
      case 'set_setpoint_globale':
        this.impianto.zone.filter((z) => !z.sovrascritta).forEach((z) => {
          z.setpoint = comando.valore;
        });
        break;
      case 'set_modalita':
        this.impianto.modalita = comando.modalita;
        this.registraEvento('modalita', comando.modalita);
        break;
      case 'set_sanitario':
        if (comando.setpoint !== undefined) this.sanitario.setpoint = comando.setpoint;
        if (comando.boost !== undefined) {
          this.sanitario.boostFinoA = comando.boost ? this.orologio() + this.impianto.sanitario.boostMinuti * 60_000 : null;
          this.registraEvento('sanitario', comando.boost ? 'boost_avviato' : 'boost_annullato');
        }
        break;
      case 'set_vmc':
        this.impianto.vmc.modalita = comando.modalita;
        break;
      case 'applica_profilo': {
        const sp = SETPOINT_PROFILO[comando.profilo];
        this.impianto.zone.forEach((z) => {
          z.setpoint = Math.min(z.limiti.max, Math.max(z.limiti.min, sp));
          z.sovrascritta = false;
        });
        this.registraEvento('profilo', comando.profilo);
        break;
      }
      case 'set_programma':
        this.programmi.set(comando.zonaId, structuredClone(comando.programma));
        break;
    }
    this.regola();
  }

  // ───────────────────────────── area tecnica ─────────────────────────────

  private verifica(sessione: SessioneTecnica): SessioneTecnica {
    const attiva = this.sessioni.get(sessione.token);
    if (!attiva) throw new ErroreAutorizzazione('non_autorizzato');
    if (attiva.scadenza < this.orologio()) {
      this.sessioni.delete(sessione.token);
      throw new ErroreAutorizzazione('sessione_scaduta');
    }
    attiva.scadenza = this.orologio() + INATTIVITA_SESSIONE_MS;
    return attiva;
  }

  private registra(operatore: string, azione: string, oggetto: string, prima: unknown, dopo: unknown): void {
    this.registro.push({
      id: this.nuovoId('reg'),
      ts: this.orologio(),
      operatore,
      azione,
      oggetto,
      prima: prima === undefined || prima === null ? null : JSON.stringify(prima),
      dopo: dopo === undefined || dopo === null ? null : JSON.stringify(dopo),
    });
  }

  private rientraForzatura(canale: string, motivo: 'timeout' | 'annullata', operatore = 'centralina'): void {
    const f = this.forzature.get(canale);
    if (!f) return;
    clearTimeout(f.timer);
    this.forzature.delete(canale);
    const attuale = this.attuatori.get(canale);
    if (attuale) {
      const nuovo: StatoAttuatore = { canale, ts: this.orologio(), stato: 'spento', modulazione: attuale.modulazione === undefined ? undefined : 0 };
      this.attuatori.set(canale, nuovo);
      if (this.raggiungibile) this.emetti({ tipo: 'attuatore', ts: nuovo.ts, stato: nuovo });
    }
    this.registra(operatore, motivo === 'timeout' ? 'rientro_forzatura' : 'annulla_forzatura', canale, attuale?.stato ?? null, 'spento');
    this.registraEvento('forzatura', `${motivo}:${canale}`);
    this.regola();
  }

  private creaAreaTecnica(): AreaTecnicaClient {
    return {
      accedi: async (credenziali: CredenzialiTecnico) => {
        await new Promise((r) => setTimeout(r, 300));
        const voce = this.credenziali[credenziali.utente];
        if (!voce || voce.password !== credenziali.password) throw new ErroreAutorizzazione('credenziali_errate');
        const sessione: SessioneTecnica = {
          token: `${this.impiantoId}-${this.nuovoId('tok')}-${this.orologio().toString(36)}`,
          operatore: voce.operatore,
          ruolo: voce.ruolo,
          scadenza: this.orologio() + INATTIVITA_SESSIONE_MS,
        };
        this.sessioni.set(sessione.token, sessione);
        this.registra(sessione.operatore, 'accesso_area_tecnica', 'sessione', null, sessione.ruolo);
        return { ...sessione };
      },

      getCanali: async (sessione) => {
        this.verifica(sessione);
        return this.impianto.canali.map<LetturaCanale>((c) => {
          if (c.direzione === 'OUTPUT') {
            const a = this.attuatori.get(c.id);
            const v = a ? (a.modulazione !== undefined ? a.modulazione : a.stato === 'attivo' ? 1 : 0) : null;
            return { canale: structuredClone(c), grezzo: v, convertito: v, q: 'ok', ts: a?.ts ?? this.orologio() };
          }
          const lettura = this.ultimeLetture[c.id];
          const fisico = this.valori.get(c.id);
          if (this.sondeScollegate.has(c.id) || fisico === undefined) {
            return { canale: structuredClone(c), grezzo: this.sondeScollegate.has(c.id) ? 4095 : null, convertito: null, q: 'sonda_scollegata', ts: lettura?.ts ?? this.orologio() };
          }
          const grezzo = c.grandezza === 'TEMPERATURA' ? contiAdc(fisico) : Math.round((fisico / 100) * 4095);
          return { canale: structuredClone(c), grezzo, convertito: arrotonda(applicaTaratura(fisico, c.taratura)), q: 'ok', ts: lettura?.ts ?? this.orologio() };
        });
      },

      patchCanale: async (sessione, canaleId, patch: PatchCanale) => {
        const s = this.verifica(sessione);
        const c = this.impianto.canali.find((x) => x.id === canaleId);
        if (!c) throw new ErroreRichiesta('canale_inesistente');
        if (patch.taratura) {
          const nuova = { ...c.taratura, ...patch.taratura };
          if (nuova.offset !== c.taratura.offset) this.registra(s.operatore, 'taratura_offset', canaleId, c.taratura.offset, nuova.offset);
          if (nuova.fattore !== c.taratura.fattore) this.registra(s.operatore, 'taratura_fattore', canaleId, c.taratura.fattore, nuova.fattore);
          c.taratura = nuova;
        }
        if (patch.tipoSonda && patch.tipoSonda !== c.tipoSonda) {
          this.registra(s.operatore, 'tipo_sonda', canaleId, c.tipoSonda, patch.tipoSonda);
          c.tipoSonda = patch.tipoSonda;
        }
        if (patch.assegnazione && JSON.stringify(patch.assegnazione) !== JSON.stringify(c.assegnazione)) {
          this.registra(s.operatore, 'assegnazione_canale', canaleId, c.assegnazione, patch.assegnazione);
          if (c.direzione === 'INPUT' && c.assegnazione.tipo === 'zona') {
            const vecchia = this.impianto.zone.find((z) => z.id === (c.assegnazione as { zonaId: string }).zonaId);
            if (vecchia && vecchia.canaleSonda === c.id) vecchia.canaleSonda = '';
          }
          c.assegnazione = patch.assegnazione;
          if (c.direzione === 'INPUT' && patch.assegnazione.tipo === 'zona') {
            const zona = this.impianto.zone.find((z) => z.id === (patch.assegnazione as { zonaId: string }).zonaId);
            if (zona) zona.canaleSonda = c.id;
          }
          if (patch.assegnazione.tipo !== 'nessuna') c.confidenza = 'alta';
        }
        this.registraEvento('configurazione', `canale:${canaleId}`);
        this.pubblicaLetture();
      },

      patchZonaCarico: async (sessione, zonaId, tipoCarico: TipoCarico) => {
        const s = this.verifica(sessione);
        const z = this.impianto.zone.find((x) => x.id === zonaId);
        if (!z) throw new ErroreRichiesta('zona_inesistente');
        if (z.tipoCarico !== tipoCarico) {
          this.registra(s.operatore, 'tipo_carico', z.nome, z.tipoCarico, tipoCarico);
          z.tipoCarico = tipoCarico;
          this.emettiStato();
        }
      },

      getParametri: async (sessione) => {
        this.verifica(sessione);
        return structuredClone(this.impianto.parametri);
      },

      patchParametri: async (sessione, parametri: ParametriRegolazione) => {
        const s = this.verifica(sessione);
        const esito = ParametriRegolazioneSchema.safeParse(parametri);
        if (!esito.success) throw new ErroreRichiesta('parametri_non_validi');
        const prima = this.impianto.parametri;
        const piatto = (o: unknown, prefisso = ''): Record<string, unknown> => {
          if (o === null || typeof o !== 'object') return { [prefisso]: o };
          return Object.entries(o as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [k, v]) => Object.assign(acc, piatto(v, prefisso ? `${prefisso}.${k}` : k)), {});
        };
        const a = piatto(prima);
        const b = piatto(esito.data);
        Object.keys(b).forEach((k) => {
          if (a[k] !== b[k]) this.registra(s.operatore, 'parametro', k, a[k], b[k]);
        });
        this.impianto.parametri = esito.data as ParametriRegolazione;
        this.registraEvento('configurazione', 'parametri');
      },

      getGeneratori: async (sessione) => {
        this.verifica(sessione);
        return structuredClone(this.impianto.generatori);
      },

      putGeneratori: async (sessione, generatori: Generatore[]) => {
        const s = this.verifica(sessione);
        if (generatori.length < 1 || generatori.length > MAX_GENERATORI) throw new ErroreRichiesta('numero_generatori');
        const priorita = new Set(generatori.map((g) => g.priorita));
        if (priorita.size !== generatori.length) throw new ErroreRichiesta('priorita_duplicata');
        this.registra(s.operatore, 'generatori', 'impianto', this.impianto.generatori.map((g) => `${g.priorita}:${g.nome}`), generatori.map((g) => `${g.priorita}:${g.nome}`));
        this.impianto.generatori = structuredClone(generatori);
        this.generatoriAttivi.clear();
        this.registraEvento('configurazione', 'generatori');
      },

      forzatura: async (sessione, richiesta: RichiestaForzatura) => {
        const s = this.verifica(sessione);
        const esito = RichiestaForzaturaSchema.safeParse(richiesta);
        if (!esito.success) throw new ErroreRichiesta('timeoutSec_obbligatorio');
        const attuale = this.attuatori.get(richiesta.canale);
        if (!attuale) throw new ErroreRichiesta('canale_inesistente');
        const esistente = this.forzature.get(richiesta.canale);
        if (esistente) clearTimeout(esistente.timer);
        const fino = this.orologio() + richiesta.timeoutSec * 1000;
        const timer = setTimeout(() => this.rientraForzatura(richiesta.canale, 'timeout'), richiesta.timeoutSec * 1000);
        this.forzature.set(richiesta.canale, { fino, timer });
        const nuovo: StatoAttuatore = { canale: richiesta.canale, ts: this.orologio(), stato: richiesta.stato, modulazione: richiesta.modulazione ?? attuale.modulazione, forzatoFinoA: fino };
        this.attuatori.set(richiesta.canale, nuovo);
        this.registra(s.operatore, 'forzatura', richiesta.canale, attuale.stato, `${richiesta.stato} (${richiesta.timeoutSec}s)`);
        this.registraEvento('forzatura', `avvio:${richiesta.canale}`);
        if (this.raggiungibile) this.emetti({ tipo: 'attuatore', ts: nuovo.ts, stato: nuovo });
      },

      annullaForzatura: async (sessione, canale) => {
        const s = this.verifica(sessione);
        this.rientraForzatura(canale, 'annullata', s.operatore);
      },

      getRegistro: async (sessione) => {
        this.verifica(sessione);
        return [...this.registro].reverse();
      },

      getOta: async (sessione) => {
        this.verifica(sessione);
        return { ...this.ota };
      },

      avviaOta: async (sessione, opzioni) => {
        const s = this.verifica(sessione);
        if (!opzioni.firmata) {
          this.ota = { ...this.ota, fase: 'rifiutato', avanzamentoPct: 0, firmata: false, ultimoEsito: 'immagine_non_firmata' };
          this.registra(s.operatore, 'ota_rifiutato', 'firmware', this.ota.versioneCorrente, 'immagine_non_firmata');
          this.registraEvento('ota', 'rifiutato:immagine_non_firmata');
          return { ...this.ota };
        }
        if (!this.ota.versioneDisponibile) throw new ErroreRichiesta('nessun_aggiornamento');
        if (this.otaTimer) clearTimeout(this.otaTimer);
        this.ota = { ...this.ota, fase: 'pubblicazione', avanzamentoPct: 0, firmata: true, ultimoEsito: undefined };
        this.registra(s.operatore, 'ota_avvio', 'firmware', this.ota.versioneCorrente, this.ota.versioneDisponibile);
        this.registraEvento('ota', `avvio:${this.ota.versioneDisponibile}`);
        const passo = () => {
          if (this.ota.fase === 'pubblicazione') {
            this.ota = { ...this.ota, fase: 'rollout', avanzamentoPct: 5 };
          } else if (this.ota.fase === 'rollout') {
            const pct = Math.min(100, this.ota.avanzamentoPct + 12);
            this.ota = { ...this.ota, avanzamentoPct: pct, fase: pct >= 100 ? 'conferma' : 'rollout' };
          } else if (this.ota.fase === 'conferma') {
            const nuova = this.ota.versioneDisponibile!;
            this.ota = { versioneCorrente: nuova, versionePrecedente: this.ota.versioneCorrente, versioneDisponibile: null, fase: 'completato', avanzamentoPct: 100, firmata: true };
            this.impianto.centralina.firmware = nuova;
            this.registraEvento('ota', `completato:${nuova}`);
            return;
          }
          this.otaTimer = setTimeout(passo, 700);
        };
        this.otaTimer = setTimeout(passo, 1200);
        return { ...this.ota };
      },

      rollbackOta: async (sessione) => {
        const s = this.verifica(sessione);
        if (!this.ota.versionePrecedente) throw new ErroreRichiesta('nessuna_versione_precedente');
        const corrente = this.ota.versioneCorrente;
        this.ota = { versioneCorrente: this.ota.versionePrecedente, versionePrecedente: null, versioneDisponibile: corrente, fase: 'rollback', avanzamentoPct: 0, firmata: true };
        this.impianto.centralina.firmware = this.ota.versioneCorrente;
        this.registra(s.operatore, 'ota_rollback', 'firmware', corrente, this.ota.versioneCorrente);
        this.registraEvento('ota', `rollback:${this.ota.versioneCorrente}`);
        this.otaTimer = setTimeout(() => {
          this.ota = { ...this.ota, fase: 'inattivo' };
        }, 1500);
        return { ...this.ota };
      },
    };
  }

  // ───────────────────────────── storico ─────────────────────────────

  private profiliStorico(): Record<string, ProfiloCanale> {
    const profili: Record<string, ProfiloCanale> = {};
    this.impianto.canali.filter((c) => c.direzione === 'INPUT').forEach((c) => {
      const zona = this.zonaDellaSonda(c.id);
      const f = this.funzioneDi(c);
      if (zona) profili[c.id] = { base: zona.setpoint - 0.3, ampiezzaGiornaliera: 0.8, oraPicco: 19, rumore: 0.3 };
      else if (f === 'MANDATA_COLLETTORE') profili[c.id] = { base: 36, ampiezzaGiornaliera: 4, oraPicco: 19, rumore: 1 };
      else if (f === 'RITORNO_COLLETTORE') profili[c.id] = { base: 29.5, ampiezzaGiornaliera: 3.5, oraPicco: 19, rumore: 1 };
      else if (f === 'ACCUMULO_SANITARIO') profili[c.id] = { base: 46, ampiezzaGiornaliera: 3, oraPicco: 8, rumore: 0.8 };
      else if (f === 'MANDATA_GENERATORE') profili[c.id] = { base: 38, ampiezzaGiornaliera: 5, oraPicco: 19, rumore: 1.2 };
      else if (f === 'RITORNO_GENERATORE') profili[c.id] = { base: 31, ampiezzaGiornaliera: 4, oraPicco: 19, rumore: 1 };
      else if (f === 'PUFFER_ALTO') profili[c.id] = { base: 45, ampiezzaGiornaliera: 4, oraPicco: 15, rumore: 0.6 };
      else if (f === 'PUFFER_BASSO') profili[c.id] = { base: 37, ampiezzaGiornaliera: 4, oraPicco: 15, rumore: 0.6 };
      else if (f === 'TEMPERATURA_ESTERNA') profili[c.id] = { base: 10.5, ampiezzaGiornaliera: 4, oraPicco: 14, rumore: 0.5 };
      else if (f === 'UMIDITA_INTERNA') profili[c.id] = { base: 45, ampiezzaGiornaliera: 4, oraPicco: 6, rumore: 1.5, min: 0 };
      else if (f === 'LUMINOSITA') profili[c.id] = { base: 35, ampiezzaGiornaliera: 45, oraPicco: 13, rumore: 3, min: 0 };
      else if (this.sondeScollegate.has(c.id)) profili[c.id] = { base: 0, ampiezzaGiornaliera: 0, oraPicco: 0, rumore: 0, assente: true };
    });
    if (this.impianto.energia.presente) {
      const picco = (this.impianto.energia.potenzaPiccoKw ?? 6) * 1000;
      profili['energia.produzione'] = { base: picco * 0.25, ampiezzaGiornaliera: picco * 0.45, oraPicco: 13, rumore: picco * 0.08, min: 0 };
      profili['energia.consumo'] = { base: 1500, ampiezzaGiornaliera: 900, oraPicco: 7, rumore: 300, min: 200 };
      profili['energia.rete'] = { base: 800, ampiezzaGiornaliera: -2500, oraPicco: 13, rumore: 300 };
    }
    return profili;
  }
}
